import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { MatPaginator, MatPaginatorModule } from "@angular/material/paginator";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    finalize,
    Observable,
    Subject,
    takeUntil
} from "rxjs";
import { fuseAnimations } from "@fuse/animations";
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxSpinnerComponent, NgxSpinnerService } from 'ngx-spinner';
import {UserManagementComponent} from "../user-management/user-management.component";
import {RestFactoryService} from "../../../core/services/rest-factory.service";

export interface AccountElement {
    id_user: string;
    ds_firstname: string;
    ds_middlename: string;
    ds_lastname: string;
    ds_email: string;
    en_logintype: number;
    en_accountstatus: number;
    vi_loginattempts: number;
    dt_created: {
        datetime: {
            date: string;
            timezone_type: number;
            timezone: string;
        }
    };
}

@Component({
    selector: "accounts-list",
    templateUrl: "./accounts-list.component.html",
    styleUrls: ['./accounts-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: fuseAnimations,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatPaginatorModule,
        MatSelectModule,
        ReactiveFormsModule,
        NgxSpinnerComponent,
        UserManagementComponent
    ]
})
export class AccountsListComponent implements OnInit, OnDestroy {

    @ViewChild(MatPaginator) private _paginator: MatPaginator;

    searchForm: UntypedFormGroup;
    loadingMessage: string = "Loading...";
    title: string = "User Accounts";

    tableElements: AccountElement[] = [];
    showFilters: boolean = false;
    showManagement: boolean = false;
    selectedUserId: string | null = null;

    accountTypes = [
        { value: -1, view: 'All' },
        { value: 1, view: 'Member' },
        { value: 2, view: 'Destination Owner' },
        { value: 3, view: 'Admin Console' },
        { value: 4, view: 'Ambassador' }
    ];

    private readonly API_GET_ACCOUNTS = "/admin/account/search";
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _changeDetectorRef: ChangeDetectorRef,
        private _restFactoryService: RestFactoryService,
        private spinner: NgxSpinnerService
    ) {}

    ngOnInit(): void {
        this.searchForm = this._formBuilder.group({
            search: [''],
            type: [-1],
            inactive: [false]
        });

        this.searchForm.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            takeUntil(this._unsubscribeAll)
        ).subscribe(() => {
            if (this._paginator) {
                this._paginator.pageIndex = 0;
            }
            this.searchElements('filter');
        });

        this.searchElements(null);
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    searchElements(searcher: string | null): void {
        const searchValues = this.searchForm.getRawValue();
        let data = {
            "ds_search": searchValues.search,
            "en_logintype": searchValues.type,
            "bl_includeinactive": searchValues.inactive
        };

        const payload = this._restFactoryService.createPayloadPaginator(searcher, this._paginator, data, 20);
        this.tableElements = [];
        this.loadingMessage = null;
        this.spinner.show().then();

        this._restFactoryService.httpPostService(this.API_GET_ACCOUNTS, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => this.spinner.hide().then()),
                catchError((err) => {
                    this.loadingMessage = "No results found!";
                    this.tableElements = [];
                    this._changeDetectorRef.markForCheck();
                    return err;
                })
            )
            .subscribe((response) => {
                this.tableElements = response.users || [];

                if (this._paginator && response.vi_total !== undefined) {
                    this._paginator.length = response.vi_total;
                }

                if (this.tableElements.length === 0) {
                    this.loadingMessage = "No results found";
                }

                this._changeDetectorRef.markForCheck();
            });
    }

    createNewUser(): void {
        this.selectedUserId = null;
        this.showManagement = true;
        this._changeDetectorRef.markForCheck();
    }

    switchToUser(userId: string): void {
        this.selectedUserId = userId;
        this.showManagement = true;
        this._changeDetectorRef.markForCheck();
    }

    handleCommand(command: string): void {
        if (command === 'back' || command === 'saved') {
            this.showManagement = false;
            this.selectedUserId = null;
            this.searchElements('filter');
        }
        this._changeDetectorRef.markForCheck();
    }

    getFullName(element: AccountElement): string {
        return [element.ds_firstname, element.ds_middlename, element.ds_lastname]
            .filter(val => !!val)
            .join(' ') || 'Unnamed User';
    }

    getUserTypeLabel(type: number): string {
        switch (type) {
            case 1: return 'Member';
            case 2: return 'Destination Owner';
            case 3: return 'Admin Console';
            case 4: return 'Ambassador';
            default: return 'Unknown';
        }
    }

    getUserTypeIndicatorColor(type: number): string {
        switch (type) {
            case 1: return 'bg-cyan-500';
            case 2: return 'bg-indigo-500';
            case 3: return 'bg-emerald-500';
            case 4: return 'bg-amber-500';
            default: return 'bg-gray-400';
        }
    }

    getUserTypeBadgeClass(type: number): string {
        switch (type) {
            case 1: return 'text-cyan-800 bg-cyan-100 dark:text-cyan-100 dark:bg-cyan-900';
            case 2: return 'text-indigo-800 bg-indigo-100 dark:text-indigo-100 dark:bg-indigo-900';
            case 3: return 'text-emerald-800 bg-emerald-100 dark:text-emerald-100 dark:bg-emerald-900';
            case 4: return 'text-amber-800 bg-amber-100 dark:text-amber-100 dark:bg-amber-900';
            default: return 'text-gray-800 bg-gray-100 dark:text-gray-100 dark:bg-gray-700';
        }
    }

    getAccountStatusLabel(status: number): string {
        switch(status) {
            case 0: return 'Active';
            case 1: return 'Inactive';
            case 2: return 'Locked';
            case 3: return 'Deleted';
            default: return `Status: ${status}`;
        }
    }

    getAccountStatusBadgeClass(status: number): string {
        switch(status) {
            case 0: return 'text-emerald-800 bg-emerald-100 dark:text-emerald-100 dark:bg-emerald-900';
            case 1: return 'text-gray-800 bg-gray-100 dark:text-gray-100 dark:bg-gray-700';
            case 2: return 'text-amber-800 bg-amber-100 dark:text-amber-100 dark:bg-amber-900';
            case 3: return 'text-red-800 bg-red-100 dark:text-red-100 dark:bg-red-900';
            default: return 'text-gray-800 bg-gray-100 dark:text-gray-100 dark:bg-gray-700';
        }
    }
}
